import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Card, CardContent, Button, CardMedia, Container } from "@mui/material";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { donwicon, kollaheights, kollaluxorpark, madhavipuritownship, parkview, projectslider1, projectslider2 } from "../../assets";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const cardData = [
  {
    image: projectslider1,
    title: "KOLLA LUXOR EXCLUSIVE",
    location: "@CHANDANAGAR, HYDERABAD",
    ongoing: true,
    description: "A luxurious project offering premium amenities.",
    detailsRoute: "/luxor-exclusive",
  },
  {
    image: projectslider2,
    title: "KOLLA LUXURIA",
    location: "@KONDAPUR, HYDERABAD",
    ongoing: true,
    description: "Experience the epitome of luxury living.",
    detailsRoute: "/luxuria",
  },
  {
    image: kollaheights,
    title: "KOLLA LUXOR HEIGHTS",
    location: "@CHANDANAGAR, HYDERABAD",
    ongoing: false,
    description: "A meticulously crafted mid-rise residential block in Chandanagar, offers spacious 2 & 3 BHK apartments. Located near IT and pharma companies, it’s a well-planned project by the Kolla Group. These homes feature ample natural light, proper ventilation, and modern amenities like a clubhouse and swimming pool.",
    detailsRoute: "/",
  },
  {
    image: kollaluxorpark,
    title: "KOLLA LUXOR PARK",
    location: "@CHANDANAGAR, HYDERABAD",
    ongoing: false,
    description: "The Kolla Luxor Park, meticulously crafted with unique features, embodies luxury and tranquility. It offers 110 ready-to-move-in Vastu-compliant homes with contemporary architecture, premium construction, top-notch fittings, and unmatched amenities. Located near IT and pharma companies at Luxor Park, it provides the ideal blend of convenience and luxury.",
    detailsRoute: "/",
  },
  {
    image: madhavipuritownship,
    title: "MADHAVIPURI TOWNSHIP",
    location: "@CHANDANAGAR, HYDERABAD",
    ongoing: false,
    description: "Madhavapuri Township, a 62-acre well-planned gated community in Ameenpur, offers stress-free living in spacious and contemporary apartments with immaculately maintained infrastructure. Over 3000 families enjoy comfortable living in a mega township environment inspired by free-flowing architecture and space.",
    detailsRoute: "/",
  },
  {
    image: parkview,
    title: "PARK VIEW ENCLAVE",
    location: "@Secunderabad, HYDERABAD",
    ongoing: false,
    description: "A fully developed residential township in Secunderabad, offers luxury villa building opportunities. The 11-acre township is divided into plots of up to 600 sq.yd. with amenities like a central park, tennis court, indoor games area, and more. Designed with unique features and stringent quality measures, Parkview Enclave provides sophisticated and luxurious villa living.",
    detailsRoute: "/",
  },
 
];

const ProjectSliderSection: React.FC = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [activeCard, setActiveCard] = useState<{ index: number; description: string; x: number; y: number } | null>(null);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    gsap.fromTo(
      sectionRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Animate each card with a stagger effect
    gsap.fromTo(
      cardsRef.current,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2, // Delay between each card animation
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  const handleCardClick = (index: number) => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
    }

    // Toggle the active card
    if (activeCard?.index === index) {
      setActiveCard(null); // Close the description if the same card is clicked again
    } else {
      setActiveCard({ index, description: cardData[index].description, x: 0, y: 0 });

      fadeTimeoutRef.current = setTimeout(() => {
        setActiveCard(null); // Automatically close after 5 seconds
      }, 5000);
    }
  };

  return (
    <Box
      ref={sectionRef}
      sx={{
        position: "relative",
        width: "100%",
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 1, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="xl" sx={{ width: "100%", textAlign: "left" }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "600",
            fontSize: "28px",
            background: "linear-gradient(90deg, #bf742c,rgb(252, 243, 202), #c58038)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
            fontFamily: "LEMON MILK",
            mb: 4,
            textAlign: "center",
          }}
        >
          PROJECTS
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              gap: 4,
              width: "100%",
              padding: "10px 0",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { height: "4px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#666", borderRadius: "4px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "#222" },
            }}
          >
            {cardData.map((project, index) => (
              <Card
                key={index}
                ref={(el) => (cardsRef.current[index] = el)}
                sx={{
                  minWidth: "320px",
                  height: "520px",
                  boxShadow: "0 4px 8px rgba(63, 63, 63, 0.3)",
                  scrollSnapAlign: "start",
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  backgroundColor: "#000000",
                  color: "white",
                  mb: 3,
                }}
                onClick={() => !project.ongoing && handleCardClick(index)}
              >
                {/* Project Image */}
                <CardMedia
                  component="div"
                  image={project.image}
                  onClick={() => project.ongoing ? window.open(project.detailsRoute, "_blank") : null}
                  sx={{
                    height: "85%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                    filter: project.ongoing ? "none" : "grayscale(100%)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Vertical Title */}
                  <Typography
                    sx={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      position: "absolute",
                      left: 6,
                      bottom: 20,
                      fontWeight: "300",
                      fontSize: "22px",
                      letterSpacing: 1,
                      color: "white",
                      height: "100%",
                    }}
                  >
                    {project.title}
                  </Typography>

                  {/* Ongoing Badge */}
                  {(
                    <Box sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
                      <Typography
                        sx={{
                          position: "absolute",
                          bottom: 10,
                          right: 10,
                          fontWeight: "400",
                          fontSize: "18px",
                          background: "linear-gradient(90deg, #bf742c,rgb(252, 243, 202), #c58038)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontFamily: "LEMON MILK",
                          textAlign: "center",
                        }}
                      >
                        {project.ongoing ? "ONGOING" : "COMPLETED"}
                      </Typography>
                    </Box>
                  )}
                </CardMedia>

                {/* Bottom Strip for Location & Details */}
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#131313",
                    padding: "12px 16px",
                  }}
                >
                  <Typography variant="h2" sx={{ fontSize: "14px", fontWeight: "400", color: "white", mr: 2 }}>
                    {project.location}
                  </Typography>
                  <Button
                    endIcon={
                      <img
                        src={donwicon}
                        alt="Best apartments in Hyderabad by Kolla Group"
                        style={{
                          width: "16px",
                          height: "16px",
                          marginLeft: "5px",
                        }}
                      />
                    }
                    sx={{
                      backgroundColor: "rgba(186, 111, 42, 0.1)", // Subtle transparent background
                      color: "white",
                      textTransform: "uppercase", 
                      fontSize: "12px", 
                      fontWeight: 600, 
                      padding: "6px 12px", 
                      border: "1px solid #BA6F2A", 
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.3s ease", 
                      "&:hover": {
                        backgroundColor: "#BA6F2A", 
                        borderColor: "#fff", // Changes border color on hover
                        transform: "scale(1.05)", // Slight pop effect
                      },
                    }}
                    onClick={() => project.ongoing ? window.open(project.detailsRoute, "_blank") : null}
                  >
                    DETAILS
                  </Button>
                </CardContent>

                {/* Description Box */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: activeCard?.index === index ? 0 : "-100%",
                    left: 0,
                    width: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: "white",
                    padding: "20px",
                    borderRadius: "0 0 8px 8px",
                    boxShadow: "0 -4px 8px rgba(0, 0, 0, 0.3)",
                    zIndex: 1000,
                    transition: "bottom 0.5s ease",
                  }}
                >
                  {activeCard?.index === index && (
                    <>
                      <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "600", color: "#BA6F2A", mb: 1 }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "14px", fontWeight: "400", color: "white" }}>
                        {project.description}
                      </Typography>
                    </>
                  )}
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
      <style>
        {`
          @keyframes growAndRetract {
            0% {
              transform: translateY(100%);
            }
            10% {
              transform: translateY(0);
            }
            90% {
              transform: translateY(0);
            }
            100% {
              transform: translateY(100%);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProjectSliderSection;
